import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * GalaxyHero — DYMAXION interactive Spline 3D hero backdrop.
 *
 * Visual language: `galaxy-interactive-hero-section` template approved
 * by Karim (SCRUM-721, 2026-04-13). Replaces the previous
 * @paper-design/shaders-react MeshGradient implementation with an
 * interactive Spline 3D scene + dark vignette overlay.
 *
 * Trade-offs recorded:
 *   - Bundle: ~1 MB gzipped for @splinetool/react-spline + three (vs
 *     ~300 KB for @paper-design/shaders-react). We reverted to Spline
 *     at Karim's direction in the 2026-04-13 review.
 *   - Runtime CDN: fetches a scene file from prod.spline.design every
 *     page load. Cacheable but external.
 *
 * Architecture: still a regular React component that lives in the same
 * tree as its siblings. The Stage 5 "island layout messed up" defect
 * is unaffected — the fix was architectural, not shader-specific.
 */
import { Component, Suspense, lazy } from 'react';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';
// Lazy-loaded so the Spline runtime + three.js don't block the main
// bundle. Suspense boundary below renders the static fallback while
// the chunk loads, so users see something immediately.
const Spline = lazy(() => import('@splinetool/react-spline'));
/**
 * Spline scene URL. Change this to swap the 3D scene. DYMAXION's
 * current default is the template scene Karim approved on 2026-04-13;
 * swap via the `scene` prop for app-specific variations.
 */
export const DEFAULT_SPLINE_SCENE = 'https://prod.spline.design/X7la16uiinoF5Vs6/scene.splinecode';
/**
 * Static CSS fallback — rendered for prefers-reduced-motion, as the
 * Suspense fallback while the Spline runtime loads, and if the scene
 * fetch fails. Pure #000000 per Karim's 2026-04-13 review — no nebula
 * pools, no purple bleed, first paint is solid black so the form +
 * wordmark get full contrast immediately.
 */
function StaticHeroFallback() {
    return (_jsx("div", { "aria-hidden": "true", className: "absolute inset-0 w-full h-full pointer-events-none", style: { background: '#000000' } }));
}
/**
 * Spherical vignette overlay — darkens the four corners in a circular
 * pattern so the Spline scene reads as a porthole into space rather
 * than a rectangular frame. Karim asked for this on 2026-04-13 review.
 *
 * The ellipse is wider than tall to match typical viewport aspect
 * ratios (16:9 / 21:9), keeping the clear centre roughly circular
 * rather than an obvious horizontal oval.
 */
function VignetteOverlay() {
    return (_jsx("div", { "aria-hidden": "true", className: "absolute inset-0 w-full h-full pointer-events-none", style: {
            background: `
          radial-gradient(ellipse 85% 90% at center, transparent 0%, transparent 35%, rgba(0, 0, 0, 0.7) 75%, rgba(0, 0, 0, 0.95) 100%),
          linear-gradient(to bottom, transparent 60%, rgba(0, 0, 0, 0.6) 100%)
        `,
        } }));
}
class ShaderErrorBoundary extends Component {
    state = { hasError: false };
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch(err, _info) {
        // eslint-disable-next-line no-console
        console.warn('[galaxy-hero] Spline failed, using static fallback:', err.message);
    }
    render() {
        if (this.state.hasError) {
            return _jsx(StaticHeroFallback, {});
        }
        return this.props.children;
    }
}
export function GalaxyHero({ scene = DEFAULT_SPLINE_SCENE, forceStatic = false, } = {}) {
    const reduced = usePrefersReducedMotion();
    const staticMode = forceStatic || reduced;
    if (staticMode) {
        return (_jsxs("div", { className: "absolute inset-0 w-full h-full overflow-hidden bg-black pointer-events-none", "data-testid": "galaxy-hero-static", children: [_jsx(StaticHeroFallback, {}), _jsx(VignetteOverlay, {})] }));
    }
    return (_jsxs("div", { className: "absolute inset-0 w-full h-full overflow-hidden bg-black", "data-testid": "galaxy-hero-spline", style: { backgroundColor: '#000000' }, children: [_jsx("div", { "aria-hidden": "true", className: "absolute inset-0 w-full h-full", style: { backgroundColor: '#000000', zIndex: 0 } }), _jsx(StaticHeroFallback, {}), _jsx(ShaderErrorBoundary, { children: _jsx(Suspense, { fallback: _jsx(StaticHeroFallback, {}), children: _jsx("div", { className: "absolute inset-0 w-full h-full", style: { backgroundColor: '#000000' }, children: _jsx(Spline, { scene: scene, style: {
                                width: '100%',
                                height: '100%',
                                pointerEvents: 'auto',
                                backgroundColor: '#000000',
                            } }) }) }) }), _jsx(VignetteOverlay, {})] }));
}
//# sourceMappingURL=GalaxyHero.js.map